/**
 * Auth-agnostic i18n provider (DEN-99 §4): one i18next instance for the app's
 * life, never recreated. A locale change is `i18n.changeLanguage(l)` plus the
 * `<html lang>` attribute and the `locale` cookie — no HTTP. The
 * provider is auth-agnostic, so it is testable standalone.
 */
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  type ReactNode,
} from 'react';
import { I18nextProvider } from 'react-i18next';
import { createI18n } from './i18n';
import {
  buildFormatConfig,
  createFormatters,
  FormatPrefsContext,
  registerFormatters,
  type FormatPrefs,
} from './formatters';
import { readLocaleCookie, writeLocaleCookie } from './cookie';
import { DEFAULT_LOCALE } from './config';
import { useStateReducer } from '../hooks/useStateReducer';

interface LocaleContextValue {
  locale: string;
  setLocale: (locale: string) => void;
}

const LocaleContext = createContext<LocaleContextValue | null>(null);

interface I18nProviderState {
  i18n: ReturnType<typeof createI18n>;
  locale: string;
  formatPrefs: FormatPrefs | null;
}

export function I18nProvider({
  children,
  initialLocale,
}: {
  children: ReactNode;
  initialLocale?: string;
}) {
  const [{ i18n, locale, formatPrefs }, setState] = useStateReducer(
    initialLocale,
    (initial): I18nProviderState => {
      const instance = createI18n(
        initial ?? readLocaleCookie() ?? DEFAULT_LOCALE,
      );
      return { i18n: instance, locale: instance.language, formatPrefs: null };
    },
  );

  const cfg = useMemo(() => buildFormatConfig(formatPrefs), [formatPrefs]);
  useEffect(() => {
    registerFormatters(i18n, createFormatters(cfg));
  }, [i18n, cfg]);

  useEffect(() => {
    // Initial sync; `setLocale` keeps `<html lang>` in step thereafter.
    document.documentElement.lang = i18n.language;
  }, [i18n]);

  const setLocale = useCallback(
    (next: string) => {
      void i18n.changeLanguage(next);
      document.documentElement.lang = next;
      writeLocaleCookie(next);
      setState({ locale: next });
    },
    [i18n, setState],
  );

  const value = useMemo(() => ({ locale, setLocale }), [locale, setLocale]);
  const setPrefs = useCallback(
    (next: FormatPrefs | null) => setState({ formatPrefs: next }),
    [setState],
  );
  const prefsValue = useMemo(
    () => ({ prefs: formatPrefs, setPrefs }),
    [formatPrefs, setPrefs],
  );

  return (
    <I18nextProvider i18n={i18n}>
      <LocaleContext.Provider value={value}>
        <FormatPrefsContext.Provider value={prefsValue}>
          {children}
        </FormatPrefsContext.Provider>
      </LocaleContext.Provider>
    </I18nextProvider>
  );
}

export function useLocale(): LocaleContextValue {
  const ctx = useContext(LocaleContext);
  if (!ctx) {
    throw new Error('useLocale must be used within I18nProvider');
  }
  return ctx;
}

export { useTranslation as useT, Trans } from 'react-i18next';
